import { html, svg, TemplateResult, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { socialSvgs, customSvgs } from './svgs';
import encapsulatedStyles from './styles/outline-svg.encapsulated.css?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
/**
 * IMPORTANT: This is called ST-svgs instead of Outline-Svgs because of a conflict in guides.
 */
export interface SvgSet {
  [key: string]: ReturnType<typeof svg>;
}
/**
 * `customSvgs` would always come last in this object assignment
 * to ensure that it is the last to be checked for an svg with
 * any name collisions from other objects.
 */
export const svgs: SvgSet = Object.assign({}, socialSvgs, customSvgs);

@customElement('outline-svg')
export class OutlineSvg extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: encapsulatedStyles,
  });

  /**
   * The name of the svg to draw.
   *
   * @param name of the svg
   * @default null
   */
  @property({ type: String, reflect: true, attribute: 'name' })
  name: string;

  /**
   * The width and height of the svg.
   *
   * @param size in px, em, rem, %, or custom variable.
   * @default false
   */
  @property({ attribute: 'size' })
  size: string | boolean = false;

  /**
   * The fill color of the svg via the `fill` property.
   *
   * This feature may not work with all svgs.
   *
   * @param color in hex, rgb, rgba, hsl, hsla, css color name, or custom variable.
   * @default false
   */
  @property({ attribute: 'svg-fill' })
  fill: string | boolean = false;

  /**
   * The stroke color of the svg.
   *
   * This feature may not work with all svgs.
   *
   * @param stroke in hex, rgb, rgba, hsl, hsla, css color name, or custom variable.
   * @default false
   */
  @property({ attribute: 'svg-stroke' })
  stroke: string | boolean = false;

  /**
   * The fill color of the svg via the `color` property.
   *
   * This feature may not work with all svgs.
   *
   * @param color in hex, rgb, rgba, hsl, hsla, css color name, or custom variable.
   * @default false
   */
  @property({ attribute: 'svg-color' })
  color: string | boolean = false;

  /**
   * Sets aria-hidden on svg to pass some accessibility standards
   */
  @property({ type: String })
  decorative = 'true';

  /**
   * Adds aria-label on svg to pass some accessibility standards
   */
  @property({ type: String })
  label: string;

  updated() {
    this.getSvg();
    if (this.decorative === 'true' && !this.label) {
      this.setAttribute('aria-hidden', 'true');
    }
    if (this.label) {
      this.setAttribute('aria-label', `${this.label}`);
      this.setAttribute('role', 'img');
    }
  }

  getSvg(): TemplateResult {    
    return svgs[this.name];
  }

  render(): TemplateResult {
    return html`
      <style>
        ${this.fill ? html` :host { --svg-fill: ${this.fill}; } ` : ''}
        ${this.size ? html` :host { --svg-size: ${this.size}; } ` : ''}
        ${this.stroke ? html` :host { --svg-stroke: ${this.stroke}; } ` : ''}
        ${this.color ? html` :host { --svg-color: ${this.color}; } ` : ''}
      </style>
      ${this.getSvg()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-svg': OutlineSvg;
  }
}